import { Box, Button, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useDeleteContract } from '../hooks/api/use-contracts.hook';
import { ContractApiModel } from '../../../types/models/contract.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchContracts } from '../api/contracts.api';

export const Contracts = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [deleteContract, { loading: deletingContract, error: deleteError }] = useDeleteContract();

  const columns: ColDef<ContractApiModel>[] = [
    {
      field: 'number',
      colId: 'contractNumber',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'district',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['startsWith'], maxNumConditions: 1 },
    },
    { field: 'ea', filter: true, sortable: true, filterParams: { filterOptions: ['startsWith'], maxNumConditions: 1 } },
    {
      field: 'contractor.name',
      colId: 'contractorName',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'contractor.address',
      colId: 'contractorAddr',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'contractor.city',
      colId: 'contractorCity',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'county',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'pmBackPreCityCr',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'descriptionOfWork',
      colId: 'descOfWork',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'locationDescription',
      colId: 'locationDesc',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'residentEngineer.name',
      colId: 'residentEngineerName',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'residentEngineer.phone',
      colId: 'residentEngineerPhone',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'federalAidLocation',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'bidOpenDate',
      filter: 'agDateColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['lessThan', 'greaterThan', 'inRange'], maxNumConditions: 1 },
    },
    {
      field: 'awardDate',
      filter: 'agDateColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['lessThan', 'greaterThan', 'inRange'], maxNumConditions: 1 },
    },
    {
      field: 'approvalDate',
      filter: 'agDateColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['lessThan', 'greaterThan', 'inRange'], maxNumConditions: 1 },
    },
    {
      field: 'workMustStartDate',
      filter: 'agDateColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['lessThan', 'greaterThan', 'inRange'], maxNumConditions: 1 },
    },
    {
      field: 'estCompDate',
      filter: 'agDateColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['lessThan', 'greaterThan', 'inRange'], maxNumConditions: 1 },
    },
    {
      field: 'actualCompDate',
      filter: 'agDateColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['lessThan', 'greaterThan', 'inRange'], maxNumConditions: 1 },
    },
    {
      field: 'acceptanceDate',
      filter: 'agDateColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['lessThan', 'greaterThan', 'inRange'], maxNumConditions: 1 },
    },
    {
      field: 'suspendDate',
      filter: 'agDateColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['lessThan', 'greaterThan', 'inRange'], maxNumConditions: 1 },
    },
    {
      field: 'lastEstDate',
      filter: 'agDateColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['lessThan', 'greaterThan', 'inRange'], maxNumConditions: 1 },
    },
    {
      headerName: 'Actions',
      minWidth: 150,
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<ContractApiModel>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deleteContract({ contractId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Contract',
                message: `Are you sure you want to delete contract "${node.data?.number}"?`,
                error: deleteError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getContracts: Parameters<typeof InfniteScrollingGrid<ContractApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchContracts({
      ...filterQuery,
      contractor: {
        name: filterQuery?.['contractor.name'],
        address: filterQuery?.['contractor.address'],
        city: filterQuery?.['contractor.city'],
      },
      residentEngineer: {
        name: filterQuery?.['residentEngineer.name'],
        phone: filterQuery?.['residentEngineer.phone'],
      },
      cursor,
      limit,
      sort: sortQuery,
    });
    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'Contracts'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Manage Contracts</Typography>
          <Box flexGrow={1} />
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
            Add Contract
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid loading={deletingContract} columnDefs={columns} onGetRows={getContracts} />
        </Box>
      </Box>
    </Box>
  );
};
